import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { Branch } from "../../types/user.type";
import {
  ALL,
  STATUS_LABEL,
  STATUS_OPTIONS,
  TYPE_LABEL,
  TYPE_OPTIONS,
} from "./constants";
import { DemandFactorFilters } from "./useDemandAdjustmentList";

interface DemandFactorFilterProps {
  canSelectBranch: boolean;
  branches: Branch[];
  /** A branch manager's own branch, shown instead of the picker. */
  ownBranchCode: string | null;
  filters: DemandFactorFilters;
  onChange: (patch: Partial<DemandFactorFilters>) => void;
}

const FILTER_FIELD_SIZE = { xs: 12, sm: 6, md: 3 };

export default function DemandFactorFilter({
  canSelectBranch,
  branches,
  ownBranchCode,
  filters,
  onChange,
}: DemandFactorFilterProps) {
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ branchCode: event.target.value });
  };

  const handleTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const type = TYPE_OPTIONS.find((option) => option === event.target.value);
    onChange({ type: type ?? ALL });
  };

  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
    const status = STATUS_OPTIONS.find((option) => option === event.target.value);
    onChange({ status: status ?? ALL });
  };

  const handleDateChange = (date: Dayjs | null) => {
    onChange({ date });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Grid container spacing={2}>
        <Grid size={FILTER_FIELD_SIZE}>
          {canSelectBranch ? (
            <TextField
              select
              size="small"
              fullWidth
              label="สาขา"
              value={filters.branchCode}
              onChange={handleBranchChange}
            >
              <MenuItem value={ALL}>ทุกสาขา</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={branch.branch_code}>
                  {branch.branch_code} - {branch.branch_name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Stack direction="row" alignItems="center" height="100%">
              {ownBranchCode && <Chip label={`สาขา ${ownBranchCode}`} />}
            </Stack>
          )}
        </Grid>
        <Grid size={FILTER_FIELD_SIZE}>
          <TextField
            select
            size="small"
            fullWidth
            label="ประเภท"
            value={filters.type}
            onChange={handleTypeChange}
          >
            <MenuItem value={ALL}>ทั้งหมด</MenuItem>
            {TYPE_OPTIONS.map((type) => (
              <MenuItem key={type} value={type}>
                {TYPE_LABEL[type]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={FILTER_FIELD_SIZE}>
          <TextField
            select
            size="small"
            fullWidth
            label="สถานะ"
            value={filters.status}
            onChange={handleStatusChange}
          >
            <MenuItem value={ALL}>ทั้งหมด</MenuItem>
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {STATUS_LABEL[status]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={FILTER_FIELD_SIZE}>
          <DatePicker
            label="ใช้อยู่ ณ วันที่"
            value={filters.date}
            format="DD/MM/YYYY"
            onChange={handleDateChange}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}
